import React, { useState, useEffect } from "react";
import Modal from "@/components/common/modal";
import TextField from "@/components/common/text-field";
import { Button } from "@elaniintc/goes-design-system";

interface FaqSection {
  section: string;
  questions: Array<{
    id: string;
    question: string;
    category: string;
    answer: string;
  }>;
}

interface SectionModalProps {
  isOpen?: boolean;
  onClose?: () => void;
  modalType?: "create" | "edit";
  initialName?: string;
  faqsData?: FaqSection[];
  onSubmit: (result: string[]) => void;
}

export const SectionModal = ({
  isOpen = false,
  onClose = () => {},
  modalType = "create",
  initialName = "",
  faqsData = [],
  onSubmit,
}: SectionModalProps) => {
  const isEdit = modalType === "edit";
  const [name, setName] = useState(initialName);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setName(initialName);
      setError(null);
    }
  }, [isOpen, initialName]);

  const normalized = name.trim().toLowerCase();
  const existingNames = faqsData.map((item) =>
    item.section.trim().toLowerCase(),
  );
  // If editing, allow the initial name unchanged
  const isDuplicate =
    existingNames.includes(normalized) &&
    !(isEdit && normalized === initialName.trim().toLowerCase());
  const isEmpty = normalized === "";
  const isDisabled = isEmpty || isDuplicate;

  // Show error message immediately when there's a duplicate
  const displayError =
    isDuplicate && name.trim() !== ""
      ? `El nombre '${name.trim()}' ya existe.`
      : error;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isDisabled) {
      if (isDuplicate) {
        setError(`El nombre '${name.trim()}' ya existe.`);
      }
      return;
    }
    const trimmed = name.trim();
    // Retornar arreglo:
    // - En creación: [nuevoNombre, modo]
    // - En edición:   [nuevoNombre, modo, nombreInicial]
    const result = isEdit
      ? [trimmed, modalType, initialName]
      : [trimmed, modalType];
    onSubmit(result);
    onClose();
  };

  const title = isEdit ? "Renombrar sección" : "Nueva sección";
  const description = isEdit
    ? "Elige un nuevo nombre para esta sección"
    : "Elige un nombre para esta nueva sección";

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      description={description}
      modalContentClassName="max-w-[520px]"
    >
      <form
        className="flex flex-col gap-2"
        id="section-form"
        onSubmit={handleSubmit}
      >
        <TextField
          maxLength={40}
          label="Sección"
          placeholder="Escribe el nombre de la sección"
          required
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (error) setError(null);
          }}
          error={displayError ?? undefined}
        />
        <div className="grid grid-cols-2 gap-2 pt-4">
          <Button
            type="button"
            variant="tertiary"
            className="w-full font-semibold border"
            onClick={onClose}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={isDisabled}
            className="w-full font-semibold"
          >
            {isEdit ? "Guardar nombre" : "Crear sección"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
